import {
  ActionIcon,
  Badge,
  Button,
  Chip,
  Group,
  Popover,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconFilterOff, IconX } from '@tabler/icons-react';
import { FC, ReactNode, useCallback, useMemo } from 'react';

import type { OrganicStatus } from '../../data-access/types/gp-sampling-assignment';

import {
  ASSIGNMENT_STATE_OPTIONS,
  countActiveOrganicGroups,
  ORGANIC_FILTER_GROUPS,
  toggleOrganicGroup,
  isOrganicGroupActive,
} from './assignment-map-constants';
import classes from './assignment-map-filter-bar.module.css';
import {
  AssignmentFilterOptions,
  ANALYSIS_TYPE_FILTERS,
  AnalysisTypeFilter,
  AssignmentMapFilters,
  countActiveAssignmentFilters,
  EMPTY_ASSIGNMENT_MAP_FILTERS,
  WITHOUT_LABORATORY_FILTER_ID,
} from './assignment-map-filters';
import { VirtualSearchableCheckboxList } from './virtual-searchable-checkbox-list';


function toggleValue<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

interface FilterPopoverButtonProps {
  activeCount: number;
  children: ReactNode;
  disabled?: boolean;
  label: string;
  title: string;
  width?: number;
}

const FilterPopoverButton: FC<FilterPopoverButtonProps> = ({
  activeCount,
  children,
  disabled,
  label,
  title,
  width = 260,
}) => {
  const [opened, { close, open }] = useDisclosure(false);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        open();
        return;
      }
      close();
    },
    [close, open],
  );

  return (
    <Popover
      disabled={disabled}
      keepMounted={false}
      onChange={handleOpenChange}
      opened={opened}
      position="bottom-start"
      transitionProps={{ duration: 120 }}
      width={width}
    >
      <Popover.Target>
        <Button
          onClick={() => (opened ? close() : open())}
          rightSection={
            activeCount ? (
              <Badge color={activeCount ? 'orange' : 'gray'} variant="light">
                {activeCount}
              </Badge>
            ) : null
          }
          variant="default"
        >
          {label}
        </Button>
      </Popover.Target>
      <Popover.Dropdown p="sm">
        <Stack gap="sm">
          <Text fw={600} size="sm">
            {title}
          </Text>
          {children}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

interface ChipToggleGroupProps<T extends string> {
  options: { value: T; label: string }[];
  selected: T[];
  onChange: (values: T[]) => void;
}

function ChipToggleGroup<T extends string>({
  options,
  selected,
  onChange,
}: ChipToggleGroupProps<T>) {
  return (
    <Group gap={6}>
      {options.map((option) => {
        const active = selected.includes(option.value);

        return (
          <Chip
            checked={active}
            key={option.value}
            onChange={() => onChange(toggleValue(selected, option.value))}
            size="xs"
            variant="light"
          >
            {option.label}
          </Chip>
        );
      })}
    </Group>
  );
}

interface OrganicChipToggleGroupProps {
  groups: { id: string; label: string; values: OrganicStatus[] }[];
  selected: OrganicStatus[];
  onChange: (values: OrganicStatus[]) => void;
}

const OrganicChipToggleGroup: FC<OrganicChipToggleGroupProps> = ({
  groups,
  selected,
  onChange,
}) => (
  <Group gap={6}>
    {groups.map((group) => {
      const active = isOrganicGroupActive(selected, group.values);

      return (
        <Chip
          checked={active}
          key={group.id}
          onChange={() => onChange(toggleOrganicGroup(selected, group.values))}
          size="xs"
          variant="light"
        >
          {group.label}
        </Chip>
      );
    })}
  </Group>
);

interface AssignmentMapFilterBarProps {
  filters: AssignmentMapFilters;
  filterOptions: AssignmentFilterOptions;
  filteredCount: number;
  totalCount: number;
  i18n: (key: string, options?: Record<string, unknown>) => string;
  onChange: (filters: AssignmentMapFilters) => void;
  filterVisibility?: {
    organizations?: boolean;
    osOrganizations?: boolean;
    samplers?: boolean;
    laboratories?: boolean;
    organic?: boolean;
    assignmentState?: boolean;
    analysisTypes?: boolean;
  };
}

export const AssignmentMapFilterBar: FC<AssignmentMapFilterBarProps> = ({
  filters,
  filterOptions,
  filteredCount,
  totalCount,
  i18n,
  onChange,
  filterVisibility,
}) => {
  const showOrganizations = filterVisibility?.organizations ?? true;
  const showOsOrganizations = filterVisibility?.osOrganizations ?? true;
  const showSamplers = filterVisibility?.samplers ?? true;
  const showLaboratories = filterVisibility?.laboratories ?? false;
  const showOrganic = filterVisibility?.organic ?? true;
  const showAssignmentState = filterVisibility?.assignmentState ?? true;
  const showAnalysisTypes = filterVisibility?.analysisTypes ?? false;
  const activeFilterGroups = countActiveAssignmentFilters(filters);
  const hiddenCount = Math.max(0, totalCount - filteredCount);

  const organicGroups = useMemo(
    () =>
      ORGANIC_FILTER_GROUPS.map((group) => ({
        id: group.id,
        values: group.values,
        label: i18n(group.labelKey),
      })),
    [i18n],
  );

  const activeOrganicGroupCount = countActiveOrganicGroups(filters.organicStatuses);

  const laboratoryCheckboxOptions = useMemo(
    () => [
      { value: String(WITHOUT_LABORATORY_FILTER_ID), label: i18n('filterWithoutLaboratory') },
      ...filterOptions.laboratories,
    ],
    [filterOptions.laboratories, i18n],
  );
  const selectedLaboratoryIds = useMemo(
    () => [
      ...filters.laboratoryIds,
      ...(filters.includeWithoutLaboratory ? [WITHOUT_LABORATORY_FILTER_ID] : []),
    ],
    [filters.includeWithoutLaboratory, filters.laboratoryIds],
  );

  const analysisTypeOptions = useMemo(
    () =>
      ANALYSIS_TYPE_FILTERS.map((value) => ({
        value,
        label: i18n(`filterAnalysis_${value}`),
      })),
    [i18n],
  );

  const stateOptions = useMemo(
    () =>
      ASSIGNMENT_STATE_OPTIONS.map((value) => ({
        value,
        label: i18n(`filterState_${value}`),
      })),
    [i18n],
  );

  return (
    <Stack gap="sm">
      <Group gap="xs" justify="space-between" wrap="nowrap">
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Text className={classes.countMain} fw={700} size="sm">
            {i18n('filtersCount', { filtered: filteredCount, total: totalCount })}
          </Text>
          {activeFilterGroups ? (
            <Text c="orange" size="xs">
              {i18n('filtersHidden', { count: hiddenCount })}
            </Text>
          ) : (
            <Text c="dimmed" size="xs">
              {i18n('filtersAllVisible')}
            </Text>
          )}
        </Stack>
        <Tooltip label={i18n('filtersReset')}>
          <ActionIcon
            aria-label={i18n('filtersReset')}
            disabled={!activeFilterGroups}
            onClick={() => onChange({ ...EMPTY_ASSIGNMENT_MAP_FILTERS })}
            size="sm"
            variant="subtle"
          >
            <IconFilterOff size={16} stroke={1.5} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Group gap={6} wrap="wrap">
        {showOrganizations && filterOptions.organizations.length ? (
          <FilterPopoverButton
            activeCount={filters.organizationIds.length}
            label={i18n('filterOrganization')}
            title={i18n('filterOrganization')}
          >
            <VirtualSearchableCheckboxList
              onSelectedIdsChange={(organizationIds) =>
                onChange({
                  ...filters,
                  organizationIds,
                })
              }
              options={filterOptions.organizations}
              searchPlaceholder={i18n('filterSearch')}
              selectedIds={filters.organizationIds}
            />
          </FilterPopoverButton>
        ) : null}

        {showOsOrganizations && filterOptions.osOrganizations.length ? (
          <FilterPopoverButton
            activeCount={filters.osOrganizationIds.length}
            label={i18n('filterOs')}
            title={i18n('filterOs')}
            width={360}
          >
            <VirtualSearchableCheckboxList
              onSelectedIdsChange={(osOrganizationIds) =>
                onChange({
                  ...filters,
                  osOrganizationIds,
                })
              }
              options={filterOptions.osOrganizations}
              searchPlaceholder={i18n('filterSearch')}
              selectedIds={filters.osOrganizationIds}
            />
          </FilterPopoverButton>
        ) : null}

        {showSamplers && filterOptions.samplers.length ? (
          <FilterPopoverButton
            activeCount={filters.samplerIds.length}
            label={i18n('filterSampler')}
            title={i18n('filterSampler')}
          >
            <VirtualSearchableCheckboxList
              onSelectedIdsChange={(samplerIds) =>
                onChange({
                  ...filters,
                  samplerIds,
                })
              }
              options={filterOptions.samplers}
              searchPlaceholder={i18n('filterSearch')}
              selectedIds={filters.samplerIds}
            />
          </FilterPopoverButton>
        ) : null}

        {showLaboratories ? (
          <FilterPopoverButton
            activeCount={
              filters.laboratoryIds.length + (filters.includeWithoutLaboratory ? 1 : 0)
            }
            label={i18n('filterLaboratory')}
            title={i18n('filterLaboratory')}
          >
            <VirtualSearchableCheckboxList
              onSelectedIdsChange={(ids) =>
                onChange({
                  ...filters,
                  laboratoryIds: ids.filter((id) => id !== WITHOUT_LABORATORY_FILTER_ID),
                  includeWithoutLaboratory: ids.includes(WITHOUT_LABORATORY_FILTER_ID),
                })
              }
              options={laboratoryCheckboxOptions}
              searchPlaceholder={i18n('filterSearch')}
              selectedIds={selectedLaboratoryIds}
            />
          </FilterPopoverButton>
        ) : null}

        {showAnalysisTypes ? (
          <FilterPopoverButton
            activeCount={filters.analysisTypes.length}
            label={i18n('filterAnalysisType')}
            title={i18n('filterAnalysisType')}
          >
            <ChipToggleGroup<AnalysisTypeFilter>
              onChange={(analysisTypes) =>
                onChange({
                  ...filters,
                  analysisTypes,
                })
              }
              options={analysisTypeOptions}
              selected={filters.analysisTypes}
            />
          </FilterPopoverButton>
        ) : null}

        {showOrganic ? (
        <FilterPopoverButton
          activeCount={activeOrganicGroupCount}
          label={i18n('filterOrganic')}
          title={i18n('filterOrganic')}
        >
          <OrganicChipToggleGroup
            groups={organicGroups}
            onChange={(values) =>
              onChange({
                ...filters,
                organicStatuses: values,
              })
            }
            selected={filters.organicStatuses}
          />
        </FilterPopoverButton>
        ) : null}

        {showAssignmentState ? (
        <FilterPopoverButton
          activeCount={filters.assignmentStates.length}
          label={i18n('filterAssignmentState')}
          title={i18n('filterAssignmentState')}
        >
          <ChipToggleGroup
            onChange={(values) =>
              onChange({
                ...filters,
                assignmentStates: values,
              })
            }
            options={stateOptions}
            selected={filters.assignmentStates}
          />
        </FilterPopoverButton>
        ) : null}
      </Group>

      {activeFilterGroups ? (
        <Group gap={4} wrap="wrap">
          {filters.organizationIds.map((id) => {
            const label =
              filterOptions.organizations.find((option) => Number(option.value) === id)?.label ??
              String(id);

            return (
              <Badge
                className={classes.activeChip}
                key={`org-${id}`}
                onClick={() =>
                  onChange({
                    ...filters,
                    organizationIds: filters.organizationIds.filter((value) => value !== id),
                  })
                }
                rightSection={<IconX size={10} />}
                size="sm"
                variant="light"
              >
                {label}
              </Badge>
            );
          })}
          {filters.osOrganizationIds.map((id) => {
            const label =
              filterOptions.osOrganizations.find((option) => Number(option.value) === id)?.label ??
              String(id);

            return (
              <Badge
                className={classes.activeChip}
                key={`os-${id}`}
                onClick={() =>
                  onChange({
                    ...filters,
                    osOrganizationIds: filters.osOrganizationIds.filter((value) => value !== id),
                  })
                }
                rightSection={<IconX size={10} />}
                size="sm"
                variant="light"
              >
                {label}
              </Badge>
            );
          })}
          {filters.includeWithoutLaboratory ? (
            <Badge
              className={classes.activeChip}
              key="laboratory-none"
              onClick={() =>
                onChange({
                  ...filters,
                  includeWithoutLaboratory: false,
                })
              }
              rightSection={<IconX size={10} />}
              size="sm"
              variant="light"
            >
              {i18n('filterWithoutLaboratory')}
            </Badge>
          ) : null}
          {filters.laboratoryIds.map((id) => {
            const label =
              filterOptions.laboratories.find((option) => Number(option.value) === id)?.label ??
              String(id);

            return (
              <Badge
                className={classes.activeChip}
                key={`laboratory-${id}`}
                onClick={() =>
                  onChange({
                    ...filters,
                    laboratoryIds: filters.laboratoryIds.filter((value) => value !== id),
                  })
                }
                rightSection={<IconX size={10} />}
                size="sm"
                variant="light"
              >
                {label}
              </Badge>
            );
          })}
          {filters.samplerIds.map((id) => {
            const label =
              filterOptions.samplers.find((option) => Number(option.value) === id)?.label ??
              String(id);

            return (
              <Badge
                className={classes.activeChip}
                key={`sampler-${id}`}
                onClick={() =>
                  onChange({
                    ...filters,
                    samplerIds: filters.samplerIds.filter((value) => value !== id),
                  })
                }
                rightSection={<IconX size={10} />}
                size="sm"
                variant="light"
              >
                {label}
              </Badge>
            );
          })}
          {organicGroups
            .filter((group) => isOrganicGroupActive(filters.organicStatuses, group.values))
            .map((group) => (
              <Badge
                className={classes.activeChip}
                key={`organic-${group.id}`}
                onClick={() =>
                  onChange({
                    ...filters,
                    organicStatuses: toggleOrganicGroup(filters.organicStatuses, group.values),
                  })
                }
                rightSection={<IconX size={10} />}
                size="sm"
                variant="light"
              >
                {group.label}
              </Badge>
            ))}
          {filters.analysisTypes.map((analysisType) => (
            <Badge
              className={classes.activeChip}
              key={`analysis-${analysisType}`}
              onClick={() =>
                onChange({
                  ...filters,
                  analysisTypes: filters.analysisTypes.filter((value) => value !== analysisType),
                })
              }
              rightSection={<IconX size={10} />}
              size="sm"
              variant="light"
            >
              {i18n(`filterAnalysis_${analysisType}`)}
            </Badge>
          ))}
          {filters.assignmentStates.map((state) => (
            <Badge
              className={classes.activeChip}
              key={`state-${state}`}
              onClick={() =>
                onChange({
                  ...filters,
                  assignmentStates: filters.assignmentStates.filter((value) => value !== state),
                })
              }
              rightSection={<IconX size={10} />}
              size="sm"
              variant="light"
            >
              {stateOptions.find((option) => option.value === state)?.label ?? state}
            </Badge>
          ))}
        </Group>
      ) : null}
    </Stack>
  );
};
